import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DayPicker } from 'react-day-picker';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  List, Grid3X3, ArrowLeft, RefreshCw 
} from 'lucide-react';
import API from '@/utils/api';
import { cn } from '@/lib/utils';
import { 
  format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek,
  addWeeks, subWeeks
} from 'date-fns';
import CalendarAddEventModal from '@/components/home/CalendarAddEventModal';
import CalendarEventDetail from '@/components/home/CalendarEventDetail';

// Module colors
const MODULE_COLORS = {
  medical: { bg: '#F9A8D4', light: '#FCE7F3', text: '#831843', name: 'Medical' },
  dates: { bg: '#93C5FD', light: '#DBEAFE', text: '#1E40AF', name: 'Important Dates' },
  'car-maintenance': { bg: '#FDE68A', light: '#FEF3C7', text: '#92400E', name: 'Vehicles' },
  documents: { bg: '#CBD5E1', light: '#F1F5F9', text: '#334155', name: 'Documents' },
  parking: { bg: '#C4B5FD', light: '#EDE9FE', text: '#5B21B6', name: 'Parking' },
  shopping: { bg: '#86EFAC', light: '#DCFCE7', text: '#166534', name: 'Shopping' },
  calendar: { bg: '#7DD3FC', light: '#E0F2FE', text: '#0369A1', name: 'Calendar' },
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // month, week, list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get('/calendar/unified-events');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    // Update selected day events when date or events change
    const dayEvents = events.filter(event => {
      try {
        return isSameDay(parseISO(event.date), selectedDate);
      } catch {
        return false;
      }
    });
    setSelectedDayEvents(dayEvents);
  }, [selectedDate, events]);

  const handleEventAdded = useCallback(() => {
    fetchEvents();
    setShowAddModal(false);
  }, [fetchEvents]);

  const handleEventUpdated = useCallback(() => {
    fetchEvents();
    setSelectedEvent(null);
  }, [fetchEvents]);

  const handleEventDeleted = useCallback(() => {
    fetchEvents();
    setSelectedEvent(null);
  }, [fetchEvents]);

  const getEventsForDate = (date) => {
    return events.filter(event => {
      try {
        return isSameDay(parseISO(event.date), date);
      } catch {
        return false;
      }
    });
  };

  const navigatePrevious = () => {
    if (view === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getModuleColor = (module) => {
    return MODULE_COLORS[module] || MODULE_COLORS.calendar;
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-400 py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, idx) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());
            
            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(day)}
                className={cn(
                  "aspect-square p-1 rounded-lg transition-colors relative",
                  "flex flex-col items-center justify-start",
                  isCurrentMonth ? "text-slate-700" : "text-slate-300",
                  isSelected && "bg-blue-100 text-blue-700",
                  isToday && !isSelected && "bg-slate-100",
                  "hover:bg-slate-50"
                )}
              >
                <span className={cn(
                  "text-sm font-medium",
                  isToday && "font-bold"
                )}>
                  {format(day, 'd')}
                </span>
                
                {/* Event dots */}
                {dayEvents.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                    {dayEvents.slice(0, 3).map((event, i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: getModuleColor(event.module).bg }}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekEnd = endOfWeek(currentDate);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100">
          {days.map((day, idx) => {
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());
            
            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(day)}
                className={cn(
                  "p-3 text-center transition-colors border-r last:border-r-0 border-slate-100",
                  isSelected && "bg-blue-50",
                  "hover:bg-slate-50"
                )}
              >
                <div className="text-xs text-slate-400 mb-1">
                  {format(day, 'EEE')}
                </div>
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center mx-auto text-sm font-medium",
                  isToday && "bg-blue-500 text-white",
                  isSelected && !isToday && "bg-blue-100 text-blue-700"
                )}>
                  {format(day, 'd')}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Events for selected day */}
        <div className="p-4 max-h-[300px] overflow-y-auto">
          {selectedDayEvents.length > 0 ? (
            <div className="space-y-2">
              {selectedDayEvents.map((event, idx) => {
                const colors = getModuleColor(event.module);
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedEvent(event)}
                    className="p-3 rounded-lg border border-slate-100 cursor-pointer hover:shadow-sm transition-shadow"
                    style={{ borderLeftWidth: '4px', borderLeftColor: colors.bg }}
                  >
                    <p className="font-medium text-sm text-slate-800">{event.title}</p>
                    {event.time && (
                      <p className="text-xs text-slate-500 mt-0.5">{event.time}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="h-10 w-10 mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-slate-500 mb-1">
                No events on {format(selectedDate, 'EEEE, MMMM d')}
              </p>
              <p className="text-xs text-slate-400">
                Tap the + button to add something
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render list view
  const renderListView = () => {
    // Group events by date
    const groupedEvents = events
      .filter(e => {
        try {
          return parseISO(e.date) >= new Date();
        } catch {
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return parseISO(a.date) - parseISO(b.date);
        } catch {
          return 0;
        }
      })
      .reduce((acc, event) => {
        const dateKey = event.date;
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(event);
        return acc;
      }, {});

    return (
      <div className="space-y-4">
        {Object.entries(groupedEvents).slice(0, 10).map(([dateStr, dayEvents]) => (
          <div key={dateStr}>
            <h4 className="text-sm font-medium text-slate-500 mb-2">
              {format(parseISO(dateStr), 'EEEE, MMMM d')}
            </h4>
            <div className="space-y-2">
              {dayEvents.map((event, idx) => {
                const colors = getModuleColor(event.module);
                return (
                  <Card
                    key={idx}
                    className="cursor-pointer hover:shadow-sm transition-shadow"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: colors.light }}
                      >
                        <CalendarIcon className="h-5 w-5" style={{ color: colors.text }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">
                          {event.title}
                        </p>
                        <p className="text-xs text-slate-500">
                          {event.time || 'All day'}
                        </p>
                      </div>
                      <Badge
                        className="text-xs"
                        style={{ backgroundColor: colors.light, color: colors.text }}
                      >
                        {colors.name}
                      </Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
        
        {Object.keys(groupedEvents).length === 0 && (
          <div className="text-center py-12">
            <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No upcoming events</p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="mt-4 bg-blue-500 hover:bg-blue-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-lg font-semibold text-slate-800">Calendar</h1>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchEvents}
                disabled={loading}
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
              <Button
                size="sm"
                onClick={() => setShowAddModal(true)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-4 pb-24">
        {/* View Toggle & Navigation */}
        <div className="flex items-center justify-between mb-4">
          {/* Month/Week Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={navigatePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center">
              {view === 'month' && format(currentDate, 'MMMM yyyy')}
              {view === 'week' && `Week of ${format(startOfWeek(currentDate), 'MMM d')}`}
              {view === 'list' && 'Upcoming Events'}
            </span>
            <Button variant="ghost" size="sm" onClick={navigateNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            
            {view !== 'list' && (
              <Button
                variant="outline"
                size="sm"
                onClick={navigateToday}
                className="ml-2 text-xs"
              >
                Today
              </Button>
            )}
          </div>
          
          {/* View Toggle */}
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-8">
              <TabsTrigger value="month" className="text-xs px-3">
                <Grid3X3 className="h-3.5 w-3.5 mr-1" />
                Month
              </TabsTrigger>
              <TabsTrigger value="week" className="text-xs px-3">
                <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                Week
              </TabsTrigger>
              <TabsTrigger value="list" className="text-xs px-3">
                <List className="h-3.5 w-3.5 mr-1" />
                List
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Calendar View */}
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'list' && renderListView()}

        {/* Selected Day Events (for month view) */}
        {view === 'month' && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-slate-600 mb-3">
              {format(selectedDate, 'EEEE, MMMM d')}
            </h4>
            
            {selectedDayEvents.length > 0 ? (
              <div className="space-y-2">
                {selectedDayEvents.map((event, idx) => {
                  const colors = getModuleColor(event.module);
                  return (
                    <Card
                      key={idx}
                      className="cursor-pointer hover:shadow-sm transition-shadow"
                      onClick={() => setSelectedEvent(event)}
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <div
                          className="w-2 h-full min-h-[40px] rounded-full"
                          style={{ backgroundColor: colors.bg }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-800 truncate">
                            {event.title}
                          </p>
                          <p className="text-xs text-slate-500">
                            {event.time || 'All day'} • {colors.name}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-slate-50/50 border-dashed">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-slate-400">No events on this day</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                    className="mt-2 text-blue-600"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add event
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Color Legend */}
        <div className="mt-6 p-4 bg-white rounded-xl border border-slate-100">
          <h4 className="text-xs font-medium text-slate-400 mb-3">Color Legend</h4>
          <div className="flex flex-wrap gap-3">
            {Object.entries(MODULE_COLORS).map(([key, colors]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colors.bg }}
                />
                <span className="text-xs text-slate-500">{colors.name}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Add Event Modal */}
      <CalendarAddEventModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        selectedDate={selectedDate}
        onEventAdded={handleEventAdded}
      />

      {/* Event Detail Modal */}
      <CalendarEventDetail
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent}
        onEventUpdated={handleEventUpdated}
        onEventDeleted={handleEventDeleted}
      />
    </div>
  );
};

export default CalendarPage;
